import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../AuthContext';
import { landingPathFor } from '../usePermissions';

/**
 * Gate for the internal application.
 *
 * This is a convenience for the user, not a security control — the server
 * rejects unauthenticated API calls regardless of what the client renders.
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
};

/**
 * Gate for a route that needs a capability, not just a session.
 *
 * Hiding a nav item stops people stumbling onto a page; it does nothing about
 * a typed URL or a stale bookmark. Without this, a Org Admin opening
 * /organizations got the page chrome and a bare "You do not have permission"
 * from the first API call — which looks like a fault rather than a boundary.
 * The server refuses either way; this is about not showing a broken page.
 *
 * Someone who lacks the capability goes to the first page they can use, which
 * is never the page they were refused, so this cannot loop.
 */
export const RequireCapability = ({ capability, anyOf, children }) => {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const held = user.permissions || [];
  const allowed = anyOf
    ? anyOf.some((entry) => held.includes(entry))
    : held.includes(capability);

  return allowed ? children : <Navigate to={landingPathFor(user)} replace />;
};

export default ProtectedRoute;
